import { Box } from '@mui/material';
import { ColumnMovedEvent, ColumnResizedEvent } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { PricelistDto, ResourceType, UserPermissionsDto } from '../../common/PricePrgClient';
import { GuardedColDef, TableSettingsColumn } from '../../common/datagrid';
import { setupGrid } from '../../common/datagrid/agGridUtils';
import { useAgTable } from '../../common/datagrid/hooks/useAgTable';
import '../../common/datagrid/table-style.css';
import { RootState } from '../../store';
import { canSeeStockColumn, getPricelistSettings, notNull } from '../../utils';
import { getPricelists } from '../pricelist/redux/pricelistSelectors';
import { getCurrentUserPermissions } from '../user/redux/userSelectors';
import { InactivatePriceColumnFormatter } from './InactivatePriceColumnFormatter';
import { inactivatePriceTableInitialColumns } from './InactivatePriceTableColumns';
import { InactivatePriceRow } from './inactivatePriceTypes';
import {
    getInactivatePriceRows,
    getInactivatePricesTable,
    getInactivatePricesTableSelectedPricelistDate,
    getInactivatePricesTableSelectedPricelistIds
} from './redux/inactivatePricesSelectors';
import { activatePrice, inactivatePricesTableActions } from './redux/inactivatePricesSlices';

const mapStateToProps = (state: RootState) => ({
    selectedPricelistIds: getInactivatePricesTableSelectedPricelistIds(state),
    table: getInactivatePricesTable(state),
    priceRows: getInactivatePriceRows(state),
    pricelists: getPricelists(state),
    userPermissions: getCurrentUserPermissions(state),
    selectedDate: getInactivatePricesTableSelectedPricelistDate(state)
});

type Props = ReturnType<typeof mapStateToProps>;
const createInactivatePriceTablePricelistColumn = (
    pricelist: PricelistDto,
    onChange: (productId: number, pricelistId: number, isDisabled: boolean) => void,
    isEditable: boolean
): GuardedColDef<InactivatePriceRow> => ({
    field: String(pricelist.pricelistId),
    headerName: pricelist.name,
    width: 100,
    editable: false,
    cellStyle: { height: '26px', borderBottomWidth: '2px' },
    cellRenderer: props => (
        <InactivatePriceColumnFormatter
            onChange={onChange}
            pricelist={pricelist}
            isEditable={isEditable}
            {...props.data}
        />
    )
});

const getAgColumns = (
    columns: TableSettingsColumn[],
    selectedPricelistIds: number[],
    pricelists: { [pricelistId: number]: PricelistDto },
    onChange: (productId: number, pricelistId: number, isInactive: boolean) => void,
    userPermissions: UserPermissionsDto
): GuardedColDef<InactivatePriceRow>[] => {
    const staticColumns = columns
        .filter(colDef => canSeeStockColumn(colDef.key, userPermissions))
        .map(c => inactivatePriceTableInitialColumns[c.key])
        .filter(notNull);

    const pricelistPermissions = userPermissions.recordPermissions.find(
        x => x.resourceType === ResourceType.Pricelist
    );

    const hasPermissionToEdit = (plId: number) => {
        if (userPermissions.isAdmin) {
            return true;
        }

        return pricelistPermissions?.recordPermissions.some(x => x.recordId === plId && x.canUpdate) ?? false;
    };

    const pricelistColumns: GuardedColDef<InactivatePriceRow>[] = selectedPricelistIds
        .map(plId => pricelists[plId])
        .filter(notNull)
        .map(pl => {
            const hasWritePermission = hasPermissionToEdit(pl.pricelistId);
            const pricelistSettings = getPricelistSettings(pricelists[pl.pricelistId], pricelists);

            const readonlyReasons: string[] = [];

            if (!hasWritePermission) {
                readonlyReasons.push("You don't have permission to edit prices on this pricelist.");
            }

            if (pricelistSettings?.isReadonly) {
                readonlyReasons.push('This pricelist is readonly.');
            }

            const isEditable = readonlyReasons.length === 0;

            return {
                ...createInactivatePriceTablePricelistColumn(pl, onChange, isEditable),
                resizable: true
            };
        });

    const cols = [...staticColumns, ...pricelistColumns];

    return columns
        .filter(c => !c.hide)
        .map(columnSetting => {
            const column = cols.find(col => col.field === columnSetting.key);
            if (column == null) {
                return null;
            }
            return { ...column, width: columnSetting?.width ?? column.width };
        })
        .filter(notNull);
};

const rowKeyGetter = (row: InactivatePriceRow) => row.productId.toString();

const InactivatePricesTable = ({
    table,
    priceRows,
    selectedPricelistIds,
    pricelists,
    userPermissions
}: Props) => {
    const dispatch = useDispatch();

    const gridRef = useRef<AgGridReact>(null);

    const onPriceActivationChanged = useCallback(
        (productId: number, pricelistId: number, isInactive: boolean) => {
            dispatch(
                activatePrice.request({
                    isActive: !isInactive,
                    productId: productId,
                    pricelistId: pricelistId
                })
            );
        },
        [dispatch]
    );

    const columns = useMemo(
        () =>
            getAgColumns(
                table.columns,
                selectedPricelistIds,
                pricelists,
                onPriceActivationChanged,
                userPermissions
            ),
        [table, selectedPricelistIds, pricelists, onPriceActivationChanged, userPermissions]
    );

    const { resizableColumns: draggableColumns, saveColumnWidth } = useAgTable({
        columns,
        rows: priceRows,
        tableActions: inactivatePricesTableActions,
        tableSettings: table,
        gridRef: gridRef
    });

    const [draggedColumn, setDraggedColumn] = useState(null as { colId: string; toIndex: number } | null);

    return (
        <Box
            sx={{
                '& [role="columnheader"]': {
                    lineHeight: '20px',
                    paddingTop: '5px'
                },
                height: '82vh'
            }}
        >
            <AgGridReact
                className="ag-theme-alpine"
                columnDefs={draggableColumns}
                rowData={priceRows}
                onFirstDataRendered={setupGrid}
                containerStyle={{
                    height: '82vh',
                    // Always show y-scroll because it fixes flickering issues
                    overflowY: 'scroll'
                }}
                headerHeight={50}
                floatingFiltersHeight={30}
                onDragStopped={() => {
                    if (draggedColumn != null && draggedColumn.colId != '') {
                        dispatch(
                            inactivatePricesTableActions.columnsReordered({
                                sourceColumn: draggedColumn?.colId,
                                targetColumn: columns[draggedColumn?.toIndex].field ?? ''
                            })
                        );
                        setDraggedColumn(null);
                    }
                }}
                onColumnResized={(event: ColumnResizedEvent<InactivatePriceRow>) => {
                    if (
                        event.finished &&
                        event.source == 'uiColumnDragged' &&
                        event.type == 'columnResized'
                    ) {
                        const colState = event.columnApi.getColumnState();

                        const indexOfCol = colState.findIndex(c => c.colId == event.column?.getColId());
                        saveColumnWidth(indexOfCol, colState[indexOfCol]?.width ?? 80);
                    }
                }}
                onColumnMoved={(event: ColumnMovedEvent<InactivatePriceRow>) => {
                    setDraggedColumn({ colId: event.column?.getColId() ?? '', toIndex: event.toIndex ?? 0 });
                }}
                getRowId={params => rowKeyGetter(params.data)}
                ref={gridRef}
            />
        </Box>
    );
};

export default connect(mapStateToProps)(InactivatePricesTable);
